import { CheckIcon, XIcon } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"
import { Link } from "~/components/Link"
import { Editor } from "~/components/editor/Editor"
import type { MentionAutocompleteUser } from "~/components/editor/plugins/MentionAutocomplete/utils"
import { Button } from "~/components/ui/button"
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger
} from "~/components/ui/dialog"
import { Field, FieldError, FieldLabel } from "~/components/ui/field"
import { getEditorPlainText } from "~/lib/utils"
import { appShortcuts, isModifiedShortcut } from "~/lib/keyboard-shortcuts"
import { ContentTemplateKind, IssueStatus } from "~/lib/enums"
import { usePermission } from "~/hooks/usePermission"
import { TeamPermissions } from "~/lib/permissions"
import type { useOnboardingData } from "~/hooks/useOnboardingData"
import { api } from "~/trpc/react"
import { IssueSetting, issueSettingAssignees } from "./IssueSetting"

export function QueueReviewButton({
	issueId,
	teamId,
	decision,
	assigneeId,
	disabled,
	onReviewed,
	mentionUsers,
	practice
}: {
	practice?: ReturnType<typeof useOnboardingData>
	issueId: string
	teamId: string
	decision: "accept" | "decline"
	assigneeId?: string | null
	disabled?: boolean
	onReviewed?: () => void
	mentionUsers?: MentionAutocompleteUser[]
}) {
	const [open, setOpen] = useState(false)
	const [comment, setComment] = useState("")
	const [selectedAssigneeId, setSelectedAssigneeId] = useState<string | null>(
		assigneeId ?? null
	)
	const { hasTeamPermission, teamLoaded } = usePermission({
		activeTeamId: teamId
	})
	const canReview =
		!!practice || (teamLoaded && hasTeamPermission(TeamPermissions.EDIT_ISSUES))
	const canComment =
		!!practice ||
		(teamLoaded && hasTeamPermission(TeamPermissions.COMMENT_ISSUES))
	const {
		data: teamData,
		isLoading: membersLoading,
		isError: membersError
	} = api.team.get.useQuery(
		{ teamId },
		{ enabled: open && canReview && !practice }
	)
	const utils = api.useUtils()
	const review = api.issues.reviewQueue.useMutation({
		onSuccess: async (reviewed) => {
			setOpen(false)
			setComment("")
			await Promise.all([
				utils.issues.invalidate(),
				utils.user.getTeams.invalidate(),
				utils.favorites.list.invalidate()
			])
			if (reviewed.status === IssueStatus.ToDo) {
				toast.success("Issue accepted", {
					description: (
						<Button asChild variant="link" size="none">
							<Link to={reviewed.issueUrl}>View issue</Link>
						</Button>
					)
				})
			} else {
				toast.success("Issue declined")
			}
			onReviewed?.()
		},
		onError: (error) => toast.error(error.message)
	})
	const accepting = decision === "accept"
	const commentTooLong =
		canComment && getEditorPlainText(comment).trim().length > 5000
	return (
		<Dialog
			open={open}
			onOpenChange={(nextOpen) => {
				if (!review.isPending) {
					setOpen(nextOpen)
					if (nextOpen) setSelectedAssigneeId(assigneeId ?? null)
					practice?.setReviewOpen(nextOpen)
				}
			}}
		>
			<DialogTrigger asChild>
				<Button
					variant={accepting ? "default" : "outline"}
					size="sm"
					aria-label={
						accepting ? "Accept queued issue" : "Decline queued issue"
					}
					title={accepting ? "Accept queued issue" : "Decline queued issue"}
					disabled={disabled || !canReview || review.isPending}
				>
					{accepting ? <CheckIcon /> : <XIcon />}
					{accepting ? "Accept" : "Decline"}
				</Button>
			</DialogTrigger>
			<DialogContent
				asChild
				size="md"
				layout="scroll"
				aria-describedby={undefined}
			>
				<form
					onSubmit={async (event) => {
						event.preventDefault()
						if (!canReview || disabled || review.isPending || commentTooLong)
							return
						if (practice) {
							await practice.edit({
								id: issueId,
								teamId,
								status: accepting ? IssueStatus.ToDo : IssueStatus.Canceled
							})
							if (getEditorPlainText(comment).trim())
								await practice.comment(comment)
							practice.setQueueDecision(decision)
							setOpen(false)
							setComment("")
							practice.setReviewOpen(false)
							onReviewed?.()
							return
						}
						review.mutate({
							issueId,
							teamId,
							decision,
							assigneeId:
								selectedAssigneeId === (assigneeId ?? null)
									? undefined
									: selectedAssigneeId,
							comment: canComment ? comment : undefined
						})
					}}
					onKeyDownCapture={(event) => {
						if (
							!isModifiedShortcut(
								event.nativeEvent,
								appShortcuts.submit_issue.keys
							) ||
							event.nativeEvent.isComposing ||
							event.altKey ||
							event.shiftKey
						)
							return
						event.preventDefault()
						event.stopPropagation()
						if (!event.repeat) event.currentTarget.requestSubmit()
					}}
				>
					<DialogHeader variant="divided">
						<DialogTitle>
							{accepting ? "Accept queued issue" : "Decline queued issue"}
						</DialogTitle>
					</DialogHeader>
					<div className="flex min-h-0 flex-col gap-4 overflow-y-auto px-4 py-4 sm:px-6">
						{!practice && (
							<Field>
								<FieldLabel>Assignee</FieldLabel>
								<IssueSetting<string | null>
									ariaLabel="Queue issue assignee"
									activeTeamId={teamId}
									value={selectedAssigneeId}
									onChange={setSelectedAssigneeId}
									options={issueSettingAssignees(teamData?.teamMembers ?? [])}
									withSearch
									showBorder
									disabled={review.isPending || membersLoading || membersError}
								/>
								{membersError && (
									<FieldError>
										Could not load team members. You can still review without
										changing the assignee.
									</FieldError>
								)}
							</Field>
						)}
						{canComment && (
							<Field data-invalid={commentTooLong || undefined}>
								<FieldLabel>Comment (optional)</FieldLabel>
								<Editor
									compact
									autoFocus
									ariaLabel="Comment (optional)"
									variant="composer"
									templateKind={
										practice ? undefined : ContentTemplateKind.Comment
									}
									referencesEnabled={!practice}
									initialState={comment}
									onChange={setComment}
									placeholder="Add a comment to the issue…"
									disabled={review.isPending}
									teamId={practice ? undefined : teamId}
									mentionUsers={mentionUsers}
								/>
								{commentTooLong && (
									<FieldError>
										Comments must be 5,000 characters or fewer.
									</FieldError>
								)}
							</Field>
						)}
					</div>
					<DialogFooter variant="divided">
						<DialogClose asChild>
							<Button variant="ghost" disabled={review.isPending}>
								Cancel
							</Button>
						</DialogClose>
						<Button
							type="submit"
							aria-keyshortcuts="Control+Enter Meta+Enter"
							variant={accepting ? "default" : "destructive"}
							pending={review.isPending}
							disabled={!canReview || disabled || commentTooLong}
						>
							{accepting ? "Accept issue" : "Decline issue"}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}
